"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const prop_types_1 = __importDefault(require("prop-types"));
const selection_hooks_1 = require("../../selection.hooks");
const selection_types_1 = require("../../selection.types");
const SelectorsWrapper_1 = __importDefault(require("./partials/SelectorsWrapper"));
/**
 * Render all selectors available for a product
 * @param {Object} props
 * @param {string} props.productCode - The product sku to retrieve and show all attribute selectors
 * @param {boolean} props.useShortenedSelectors - Switch the display type to dropdowns instead to toggles
 * @param {string} props.dataSelectorModifier - Add a custom suffix to the data-selector attributes for the selection components
 * @param {('small' | 'medium')} props.swatchesSize - Change the size of the swatches
 * @param {string[]} props.hiddenAttributes - Attributes we won't show or take into account
 * @param {boolean} props.shortDropdowns - Dropdowns are not full height and have a scrollbar
 * @param {import('../../selection.types').customOptionProps} props.customOptionProps - Custom props passed as options to the selectors
 * @param {(props: Object) => import('react').ReactNode} props.SwatchComponent - Component to render instead of the default swatch selector
 * @param {(props: Object) => import('react').ReactNode} props.CheckboxComponent - Component to render instead of the default checkbox selector
 * @param {(props: Object) => import('react').ReactNode} props.ToggleComponent - Component to render instead of the default toggle selector
 * @param {(props: Object) => import('react').ReactNode} props.DropdownComponent - Component to render instead of the default Dropdown selector
 * @param {Object} props.epigraphArButtonProps - Object that contains Epigraph components to render
 * @param {import('react').ReactNode} props.epigraphArButtonProps.EpigraphModelViewer - Epigraph Model Viewer Component
 * @param {import('react').ReactNode} props.epigraphArButtonProps.EpigraphModelDisclaimer - Epigraph Model Disclaimer Component
 * @param {string} props.className - A custom class name for the product selectors container
 * @param {boolean} props.isOutOfStockStyling - Show strikethroughs if the relevant variant is out of stock
 * @returns {import('react').ReactNode}
 */
const ProductSelectors = ({ productCode, useShortenedSelectors, dataSelectorModifier, SwatchComponent, CheckboxComponent, ToggleComponent, DropdownComponent, swatchesSize = 'medium', hiddenAttributes = [], shortDropdowns = false, customOptionProps = {}, epigraphArButtonProps = { EpigraphModelViewer: null, EpigraphDisclaimer: null }, className = null, isOutOfStockStyling = false }) => {
    const productData = (0, selection_hooks_1.useProductData)(productCode);
    if (!productData) {
        return null;
    }
    // sort options by their sort order
    let attributeOptions = Object.values(productData.options).sort((a, b) => a.sortOrder - b.sortOrder);
    // filter out hidden options
    attributeOptions = attributeOptions.filter(option => !hiddenAttributes.includes(option.code));
    // enrich options with custom props
    if (customOptionProps) {
        attributeOptions = attributeOptions.map((option) => {
            const props = customOptionProps[option.code];
            if (props) {
                return {
                    ...option,
                    ...props
                };
            }
            return option;
        });
    }
    // switch to dropdowns if useShortenedSelectors there would be long toggles
    if (useShortenedSelectors) {
        attributeOptions = attributeOptions.map(option => {
            if (option.displayType === 'toggle' && option.values.length > 3) {
                return {
                    ...option,
                    displayType: 'dropdown'
                };
            }
            return option;
        });
    }
    // shortDropdowns are not full height and have a scrollbar
    if (shortDropdowns) {
        attributeOptions = attributeOptions.map(option => {
            if (option.displayType === 'dropdown') {
                return {
                    ...option,
                    canScroll: true
                };
            }
            return option;
        });
    }
    return ((0, jsx_runtime_1.jsx)(SelectorsWrapper_1.default, { productCode: productCode, attributeOptions: attributeOptions, swatchesSize: swatchesSize, dataSelectorModifier: dataSelectorModifier, SwatchComponent: SwatchComponent, CheckboxComponent: CheckboxComponent, ToggleComponent: ToggleComponent, DropdownComponent: DropdownComponent, epigraphArButtonProps: epigraphArButtonProps, containerClassName: className, isOutOfStockStyling: isOutOfStockStyling }));
};
ProductSelectors.propTypes = {
    /** The product sku to retrieve and show all attribute selectors */
    productCode: prop_types_1.default.string.isRequired,
    /** Add a custom suffix to the data-selector attributes for the selection components */
    dataSelectorModifier: prop_types_1.default.string,
    /** Switch the display type to dropdowns instead to toggles */
    useShortenedSelectors: prop_types_1.default.bool,
    /** Change the size of the swatches (if any) */
    swatchesSize: prop_types_1.default.oneOf(['small', 'medium']),
    /** Component to render instead of the default swatch selector */
    SwatchComponent: prop_types_1.default.node,
    /** Component to render instead of the default checkbox selector */
    CheckboxComponent: prop_types_1.default.node,
    /** Component to render instead of the default toggle selector */
    ToggleComponent: prop_types_1.default.node,
    /** Component to render instead of the default Dropdown selector */
    DropdownComponent: prop_types_1.default.node,
    /** Attributes we won't show or take into account */
    hiddenAttributes: prop_types_1.default.arrayOf(prop_types_1.default.string),
    /** Dropdowns are not full height and have a scrollbar */
    shortDropdowns: prop_types_1.default.bool,
    /** Custom props passed as options to the selectors */
    customOptionProps: prop_types_1.default.shape({
        togglePropsType: selection_types_1.toggleProps
    }),
    /** Epigraph model viewer and disclaimer passed to the selectors */
    epigraphArButtonProps: prop_types_1.default.shape({
        EpigraphModelViewer: prop_types_1.default.node,
        EpigraphDisclaimer: prop_types_1.default.node
    }),
    /** A custom class name for the product selectors container that will generate a custom class for each child component also */
    className: prop_types_1.default.string,
    /** Show strikethroughs if the relevant variant is out of stock */
    isOutOfStockStyling: prop_types_1.default.bool
};
exports.default = ProductSelectors;
//# sourceMappingURL=product-selectors.js.map